/**
 * useBmrReplayQuery — fetch the AI-decision audit chain for one BMR
 * execution (Inspector mode, v0.12.9).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface BmrReplayReview {
  audit_id: string;
  at: string;
  actor_user_id: string | null;
  engines: string[];
  findings_count: number;
  model: string;
}

export interface BmrReplayVerdict {
  audit_id: string;
  at: string;
  actor_user_id: string | null;
  finding_id: string | null;
  severity: string | null;
  engine: string | null;
  verdict: string | null;
  note: string | null;
  confidence: number | null;
}

export interface BmrReplayTimelineEvent {
  at: string;
  kind: 'BMR_AI_REVIEW_RAN' | 'BMR_FINDING_VERDICT' | string;
  audit_id: string;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
}

export interface BmrReplayResponse {
  execution_id: string;
  total_events: number;
  reviews: BmrReplayReview[];
  verdicts: BmrReplayVerdict[];
  timeline: BmrReplayTimelineEvent[];
}

export function useBmrReplayQuery(execId: string | undefined): UseQueryResult<BmrReplayResponse> {
  return useQuery<BmrReplayResponse>({
    queryKey: ['inspector', 'bmr-replay', execId],
    queryFn: ({ signal }) =>
      apiFetch<BmrReplayResponse>(`/api/v1/inspector/bmr-replay/${encodeURIComponent(execId!)}`, { signal }),
    enabled: !!execId,
    staleTime: 30_000,
  });
}
